<script setup lang="ts">
import type { FormKitNode } from '@formkit/core'

import Voucher from '@/assets/voucher.svg?component'
import BOK from '@/assets/BOK.svg?component'
import Letter from '@/assets/letter.svg?component'
import Mediapack from '@/assets/mediapack.svg?component'
import Check from '@/assets/icon-check.svg?component'

interface Form {
  email: string,
  voucher?: string,
  comment: string,
}

const { executeRecaptcha } = useGoogleRecaptcha()
const { baseUrl } = useRuntimeConfig().public
const store = useStore()
const { path } = useRoute()
const { data: user, status } = useAuth()
const { shopInformation } = storeToRefs(store)

const initialState = ref<{ email?: string }>({})

if (status.value === 'authenticated' && user.value) {
  initialState.value.email = user.value.email
}

const tel = computed(() => `tel:+48${shopInformation.value.shopInformation.phone.replaceAll(' ', '')}`)
const mailTo = computed(() => `mailto:${shopInformation.value.shopInformation.contactEmail}`)

const submitted = ref(false)

const submit = async (form: Form, node: FormKitNode) => {
  const token = await executeRecaptcha('contact')
  try {
    await $fetch('/contact', {
      method: 'POST',
      body: { form, token }
    })
    
    submitted.value = true
    node.reset({ email: form.email })
  } catch ({ data: error }) {
    node.setErrors(error.data.errors.map(({ detail }) => detail))
  }
}

useHead({
  titleTemplate: 'Kontakt - %s',
  meta: [
    { name: 'robots', content: 'index,follow' }
  ],
  link: [{ hid: 'canonical', rel: 'canonical', href: `${baseUrl}${path}` }]
})

useSchemaOrg([
  defineWebPage({  
    '@type': 'ContactPage',
  })
])
</script>

<template>
  <div class="container mx-auto">
    <Breadcrumbs :breadcrumbs="[{ name: 'Kontakt' }]" />
    <h1 class="text-24 md:text-32 font-bold mt-[30px]">
      Infolinia
    </h1>
    <div class="flex flex-col md:flex-row mt-[20px]">
      <div class="flex flex-col md:w-1/2 py-[30px] md:px-[5%]">
        <div class="flex items-center px-[15px]">
          <div>
            <Voucher />
          </div>
          <div class="flex flex-col ml-[20px]">
            <h3 class="text-20 font-medium">
              Masz już voucher?
            </h3>
            <p class="mt-[20px]">
              Obsługa online czynna całodobowo, 7&nbsp;dni w tygodniu
            </p>
          </div>
        </div>
        <NuxtLink to="/rezerwacja" class="btn btn-action mt-[20px]">
          Zarezerwuj <i class="icon-arrow-button text-10" />
        </NuxtLink>
      </div>
      <div class="flex flex-col md:w-1/2 py-[30px] md:px-[5%] bg-gray-900 text-white -mx-[15px] md:mx-0">
        <div class="flex px-[15px] md:px-0">
          <div>
            <BOK />
          </div>
          <div class="flex flex-col ml-[20px]">
            <h3 class="text-20 font-medium">
              Biuro Obsługi Klienta
            </h3>
            <p class="mt-[20px]">
              <span class="whitespace-nowrap">{{ shopInformation.shopInformation.workingHours }}</span>
            </p>
            <p class="grid grid-cols-[auto_1fr] items-center gap-x-[10px] gap-y-[20px] mt-[20px]">
              <i class="icon-phone justify-self-end" />
              <NuxtLink :to="tel" class="text-green font-bold">
                {{ shopInformation.shopInformation.phone }}
              </NuxtLink>
              <i class="icon-napisz-do-nas justify-self-end" />
              <NuxtLink :to="mailTo" class="text-green font-bold">
                {{ shopInformation.shopInformation.contactEmail }}
              </NuxtLink>
            </p>
          </div>
        </div>
      </div>
    </div>
    <h2 class="text-24 md:text-32 mt-[40px] font-bold">
      Dane firmy
    </h2>
    <div class="mt-[20px]">
      <div class="md:px-[5%]">
        <p>
          Emotivo sp. z o.o. z siedzibą w Puławach, wpisana pod numerem KRS: 0000497479 do Rejestru Przedsiębiorców, prowadzonego przez Sąd Rejonowy w Lublin-Wschód w Lublinie z siedzibą w Świdniku, VI Wydział Gospodarczy Krajowego Rejestru Sądowego
        </p>
        <div class="flex flex-col md:flex-row justify-between font-bold mt-[15px] md:ml-[40px]">
          <span class="md:w-1/2 lg:w-5/12 mt-[5px]">
            KRS: 0000497479
          </span>
          <span class="md:w-1/4 lg:w-4/12 mt-[5px]">
            NIP: {{ shopInformation.shopInformation.nip }}
          </span>
          <span class="md:w-1/4 lg:w-3/12 mt-[5px]">
            REGON: 061646803
          </span>
        </div>
      </div>
      <div class="px-[15px] md:px-[5%] bg-gray-900 text-white py-[30px] mt-[20px] -mx-[15px] md:mx-0">
        <h3 class="text-20 font-medium">
          Dane do przelewu
        </h3>
        <div class="flex flex-col md:flex-row mt-[15px] md:ml-[40px]">
          <span class="md:w-1/2 lg:w-5/12 mt-[5px]">
            {{ shopInformation.shopInformation.bankInformation }}
          </span>
          <span class="md:w-1/4 lg:w-4/12 mt-[5px]">
            SWIFT: ALBPPLPW
          </span>
          <span class="md:w-1/4 lg:w-3/12 mt-[5px]">
            IBAN: PL
          </span>
        </div>
      </div>
    </div>
    <h2 id="form" class="text-24 md:text-32 mt-[40px] font-bold">
      Formularz kontaktowy
    </h2>
    <FormKit
      type="form"
      id="contact-form"
      :value="initialState"
      #default="{ value, disabled, state: { dirty, loading } }"
      :actions="false"
      :incomplete-message="false"
      @submit-raw="submitted = false"
      @submit="submit"
      form-class="mt-[20px]"
    >
      <div class="grid grid-cols-1 md:grid-cols-[30%_50%] gap-x-[10%] justify-end grid-rows-[auto_auto_1fr]">
        <FormKit
          type="email"
          outer-class="mb-[10px]"
          name="email"
          autocomplete="email"
          label="E-mail*"
          placeholder="E-mail*"
          validation="required|email"
          validation-visibility="blur"
          :validation-messages="{
            required: 'Pole e-mail nie może być puste.',
            email: 'Błędny adres e-mail.'
          }"
        />

        <FormKit
          type="text"
          name="voucher"
          label="Numer vouchera"
          placeholder="Numer vouchera"
          autocomplete="off"
          outer-class="mb-[10px]"
        />

        <FormKit
          type="textarea"
          name="comment"
          label="Treść wiadomości*"
          placeholder="Treść wiadomości*"
          autocomplete="off"
          outer-class="mb-[10px] md:row-start-1 md:row-span-3 md:col-start-2"
          rows="8"
          :help="`${value?.comment ? value?.comment.length : 0} / 300`"
          validation="required|length:10,300"
          validation-visibility="blur"
          :validation-messages="{
            required: 'Pole wiadomość nie może być puste.',
            length: 'Pole wiadomość może zawierać od 10 do 300 znaków.'
          }"
        />
        <div>
          <FormKit
            type="checkbox"
            outer-class="mb-[10px]"
            name="rules"
            validation="accepted"
            validation-visibility="blur"
            :validation-messages="{
              accepted: 'Zaakceptuj regulamin.'
            }"
          >
            <template #suffix>
              <div>
                Wyrażam zgodę na przetwarzanie moich danych osobowych podanych w formularzu przez EMOTIVO sp. z o. o. w Puławach (24-100), ul. 6 Sierpnia 1, w sposób opisany w
                <NuxtLink to="/i/polityka-prywatnosci" target="_blank" class="underline">polityce prywatności</NuxtLink>
                w celu uzyskania odpowiedzi na pytanie, skontaktowania się na wskazane dane do korespondencji - zgodnie ze złożonym zapytaniem. Zgoda jest dobrowolna i może być w każdej chwili wycofana, kierując wiadomość na adres e-mail administratora. Wycofanie zgody nie wpływa na zgodność z prawem przetwarzania, którego dokonano na podstawie zgody przed jej wycofaniem.*
              </div>
            </template>
          </FormKit>

          <div class="text-gray-500 text-12">
            * Pole wymagane
          </div>

          <div v-if="submitted && !dirty" class="flex bg-green items-center gap-[20px] text-white px-[20px] py-[10px] mt-[10px]">
            <Check class="shrink-0" />
            Wiadomość została wysłana.
          </div>

          <FormKit
            type="submit"
            :disabled="Boolean(disabled)"
            input-class="btn btn-action disabled:pointer-events-none disabled:bg-action-disabled mt-[5px]"
          >
            <Transition enter-active-class="animate-zoom-in" leave-active-class="animate-zoom-out">
              <Spinner v-if="loading" class="w-[20px] h-[20px]" />
              <span v-else class="inline-flex flex-row flex-nowrap items-center justify-center gap-[5px]">
                Wyślij <i class="icon-arrow-button text-10" />
              </span>
            </Transition>
          </FormKit>
        </div>
      </div>
    </FormKit>
    <h2 class="text-24 md:text-32 mt-[40px] font-bold">
      Kontakt z naszym zespołem
    </h2>
    <div class="flex flex-col md:flex-row mt-[20px]">
      <div class="flex flex-col md:w-1/3 bg-gray-900 text-white py-[30px] px-[15px] md:px-[35px] -mx-[15px] md:mx-0">
        <h3 class="text-20 font-medium flex-1">
          Chcesz kupić usługi dla firmy?
        </h3>
        <div class="grid grid-cols-[auto_1fr] items-center gap-x-[10px] gap-y-[20px] mt-[80px]">
          <i class="icon-phone justify-self-end" />
          <NuxtLink to="tel:+48600763700,3" class="text-green font-bold">
            +48 600 763 700 (wew. 3)
          </NuxtLink>
          <i class="icon-napisz-do-nas justify-self-end" />
          <NuxtLink to="mailto:b2b@emotivo.pl" class="text-green font-bold">
            b2b@emotivo.pl
          </NuxtLink>
        </div>
      </div>
      <div class="flex flex-col md:w-1/3 py-[30px] px-[15px] md:px-[35px] -mx-[15px] md:mx-0">
        <h3 class="text-20 font-medium flex-1">
          Jesteś naszym Wykonawcą lub chcesz nim zostać?
        </h3>
        <div class="grid grid-cols-[auto_1fr] items-center gap-x-[10px] gap-y-[20px] mt-[80px]">
          <i class="icon-phone justify-self-end" />
          <NuxtLink to="tel:+48600763700,4" class="text-green font-bold">
            +48 600 763 700 (wew. 4)
          </NuxtLink>
          <i class="icon-napisz-do-nas justify-self-end" />
          <NuxtLink to="mailto:produkt@emotivo.pl" class="text-green font-bold">
            produkt@emotivo.pl
          </NuxtLink>
        </div>
      </div>
      <div class="flex flex-col md:w-1/3 bg-gray-900 text-white py-[30px] px-[15px] md:px-[35px] -mx-[15px] md:mx-0">
        <h3 class="text-20 font-medium flex-1">
          Potrzebujesz partnera w działaniach marketingowych?
        </h3>
        <div class="grid grid-cols-[auto_1fr] items-center gap-x-[10px] gap-y-[20px] mt-[80px]">
          <i class="icon-phone justify-self-end" />
          <NuxtLink to="tel:+48600763700,5" class="text-green font-bold">
            +48 600 763 700 (wew. 5)
          </NuxtLink>
          <i class="icon-napisz-do-nas justify-self-end" />
          <NuxtLink to="mailto:marketing@emotivo.pl" class="text-green font-bold">
            marketing@emotivo.pl
          </NuxtLink>
        </div>
      </div>
    </div>
    <h2 class="text-24 md:text-32 mt-[40px] font-bold">
      Prasa
    </h2>
    <div class="flex flex-col justify-between md:flex-row mt-[20px]">
      <div class="flex items-center flex-col md:w-1/3 py-[30px]">
        <div class="flex items-center justify-center flex-1">
          <Letter />
        </div>
        <span class="font-medium mt-[30px] md:mt-[10px]">
          Kontakt dla prasy
        </span>
        <NuxtLink :to="mailTo" class="btn btn-secondary !w-auto mt-[20px] !px-[10px]">
          Napisz
        </NuxtLink>
      </div>
      <div class="flex items-center flex-col md:w-1/3 py-[30px]">
        <div class="flex items-center justify-center flex-1">
          <Mediapack />
        </div>
        <span class="font-medium mt-[30px] md:mt-[10px]">
          Mediapack
        </span>
        <NuxtLink to="/Go-Racing_Mediapack.zip" external class="btn btn-secondary !w-auto mt-[20px] !px-[10px]">
          Pobierz
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
